import {
  normalizeCarouselMediaFromApi,
  type Post,
  type PostCarouselMediaItem,
} from "../../../../services/postService";
import { DraftPost, SocialPostData } from "../types";
import { normalizeScheduledTime } from "../../../../services/socialTokenService";
import { draftPostToSchedulerShape } from "./draftToSchedulerShape";
import { INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES } from "./instagramCarouselLimits";

/** LinkedIn share commentary limit for UGC posts (approximate safe ceiling). */
const LINKEDIN_SHARE_TEXT_MAX = 3000;

export type CarouselPublishFields = {
  post_type: "CAROUSEL";
  carousel_media: PostCarouselMediaItem[];
};

/**
 * Resolves carousel fields for social publish from draft scheduler shape vs saved Post.
 * Prefers the source with more slides when both qualify.
 */
export function resolveCarouselPayloadForPublish(
  post: Post,
  draft: DraftPost
): CarouselPublishFields | Record<string, never> {
  const scheduler = draftPostToSchedulerShape(draft);
  const fromDraft =
    scheduler.post_type === "CAROUSEL" &&
    scheduler.carousel_media &&
    scheduler.carousel_media.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES
      ? {
          post_type: "CAROUSEL" as const,
          carousel_media: scheduler.carousel_media as PostCarouselMediaItem[],
        }
      : null;

  const savedCarousel = normalizeCarouselMediaFromApi(post.carousel_media) ?? [];
  const fromSaved =
    savedCarousel.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES
      ? { post_type: "CAROUSEL" as const, carousel_media: savedCarousel }
      : null;

  if (fromDraft && fromSaved) {
    return fromDraft.carousel_media.length >= fromSaved.carousel_media.length
      ? fromDraft
      : fromSaved;
  }
  if (fromDraft) return fromDraft;
  if (fromSaved) return fromSaved;
  return {};
}

/**
 * Same shape as Post Manager → `publishAndFetchPlatformPostId(post)`: full content `Post` plus
 * canvas edits and publish intent.
 */
export function mergePostWithDraftForSocialPublish(
  post: Post,
  draft: DraftPost,
  opts: {
    publishType: "now" | "scheduled";
    scheduledTime?: string;
    /** IANA zone the wall-clock was picked in; omit for browser-local. */
    timezone?: string;
  }
): Post {
  const hashtags =
    draft.hashtags?.length > 0
      ? draft.hashtags.join(", ")
      : post.hashtags || "";

  let content = draft.content ?? post.content ?? "";
  let title = draft.title ?? post.title ?? "";
  const platform = post.platform || draft.platform;

  if (/linkedin/i.test(platform)) {
    if (content.length > LINKEDIN_SHARE_TEXT_MAX) {
      content = `${content.slice(0, LINKEDIN_SHARE_TEXT_MAX - 3)}...`;
    }
    if (title.length > 200) {
      title = `${title.slice(0, 197)}...`;
    }
  }

  const isScheduled = opts.publishType === "scheduled" && Boolean(opts.scheduledTime);
  const scheduled_time = isScheduled
    ? normalizeScheduledTime(opts.scheduledTime as string, opts.timezone)
    : "";
  const scheduled_timezone = isScheduled ? opts.timezone || "" : "";

  const carouselPayload = resolveCarouselPayloadForPublish(post, draft);

  return {
    ...post,
    content,
    title,
    image_url: draft.image_url ?? post.image_url ?? "",
    video_url: draft.video_url ?? post.video_url ?? "",
    document_url: draft.document_url ?? post.document_url ?? "",
    call_to_action: draft.call_to_action ?? post.call_to_action ?? "",
    hashtags,
    publish_type: opts.publishType,
    scheduled_time,
    scheduled_timezone,
    carousel_slides: draft.carousel_slides ?? post.carousel_slides ?? [],
    ...carouselPayload,
  };
}

type PublishPayload = {
  customer_id: string;
  content: string;
  image_url: string;
  video_url: string;
  document_url?: string;
  hashtags: string;
  call_to_action: string;
  platform: string;
  scheduled_time: string;
  /** IANA zone the schedule was picked in; empty for publish-now. */
  scheduled_timezone?: string;
  publish_type: "now" | "scheduled";
  post_id?: string;
  post_type?: string;
  carousel_media?: PostCarouselMediaItem[];
  carousel_slides?: Array<{ url: string; type?: string; caption?: string }>;
};

export function buildPublishNowPayload(
  customerId: string,
  postData: SocialPostData
): PublishPayload {
  const payload: PublishPayload = {
    customer_id: customerId,
    content: postData.content || "",
    image_url: postData.image_url || "",
    video_url: postData.video_url || "",
    document_url: postData.document_url || "",
    hashtags: postData.hashtags || "",
    call_to_action: postData.call_to_action || "",
    platform: postData.platforms?.[0] || "facebook",
    scheduled_time: "",
    publish_type: "now",
  };

  if (postData.post_id) payload.post_id = postData.post_id;

  if (postData.post_type) payload.post_type = postData.post_type;
  if (
    Array.isArray(postData.carousel_media) &&
    postData.carousel_media.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES
  ) {
    payload.carousel_media = postData.carousel_media;
  }

  if (Array.isArray(postData.carousel_slides) && postData.carousel_slides.length > 0) {
    payload.carousel_slides = postData.carousel_slides;
  }

  return payload;
}

export function buildScheduledPayload(
  customerId: string,
  postData: SocialPostData,
  scheduleDateTime: string,
  /** IANA zone the wall-clock was picked in; omit for browser-local. */
  timezone?: string
): PublishPayload {
  const tz = timezone || postData.scheduled_timezone;
  const normalizedSchedule = normalizeScheduledTime(scheduleDateTime, tz);

  const payload: PublishPayload = {
    customer_id: customerId,
    content: postData.content || "",
    image_url: postData.image_url || "",
    video_url: postData.video_url || "",
    hashtags: postData.hashtags || "",
    call_to_action: postData.call_to_action || "",
    platform: postData.platforms?.[0] || "facebook",
    scheduled_time: normalizedSchedule,
    scheduled_timezone: tz || "",
    publish_type: "scheduled",
  };

  if (postData.post_id) payload.post_id = postData.post_id;

  if (postData.post_type) payload.post_type = postData.post_type;
  if (
    Array.isArray(postData.carousel_media) &&
    postData.carousel_media.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES
  ) {
    payload.carousel_media = postData.carousel_media;
  }

  if (Array.isArray(postData.carousel_slides) && postData.carousel_slides.length > 0) {
    payload.carousel_slides = postData.carousel_slides;
  }

  return payload;
}
